/* eslint-disable no-param-reassign */
import { createRouter, createWebHashHistory } from 'vue-router';
import routesNames from '@/router/constants';
import { fetchEvent } from '@/api';
import { fetchQuestionTypes, fetchQuestionTypeById } from '@/api/eventsSurveys';
import EventsView from '@/views/EventsView';
import EventView from '@/views/EventView';
import CheckInView from '@/views/CheckInView';
import RegisterFormView from '@/views/RegisterFormView';
import EventViewUsers from '@/views/EventViewUsers';
import LoginBridge from '@/views/LoginBridge';
import EventViewSurvey from '@/views/EventViewSurvey';
import EditSurveyQuestionsView from '@/views/EditSurveyQuestionsView';
import ReorderSurveyQuestionTypesView from '@/views/ReorderSurveyQuestionTypesView';
import EventViewSettings from '@/views/EventViewSettings';
import EventViewUserFieldsEditor from '@/views/EventViewUserFieldsEditor';
import EventViewPrivacyPolicy from '@/views/EventViewPrivacyPolicy';
import EventViewCheckIn from '@/views/EventViewCheckIn';
import EventViewSurveySettings from '@/views/EventViewSurveySettings';
import EventViewImportUsers from '@/views/EventViewImportUsers';
import EventViewMarketingEmails from '@/views/EventViewMarketingEmails';
import EventViewScuConfig from '@/views/EventViewScuConfig';
import i18n from '@/i18n';
import EventViewBanner from '@/views/EventViewBanner';
import EventViewMailing from '@/views/EventViewMailing';
import { has } from 'lodash';

const routes = [
  {
    path: '/login-bridge',
    name: 'login-bridge',
    component: LoginBridge,
    props: (route) => {
      let token;

      if (has(route, 'query.token_type') && has(route, 'query.access_token')) {
        token = {
          token_type: route.query.token_type,
          access_token: route.query.access_token,
        };
      }

      return {
        token,
        profilerId: 176,
      };
    },
  },
  {
    path: '/home',
    name: 'events',
    component: EventsView,
  },
  {
    path: '/events/:id',
    name: 'event',
    component: EventView,
    meta: {
      event: null,
    },
    props: (route) => ({
      event: route.meta.event,
    }),
    children: [
      {
        path: 'settings',
        name: routesNames.EVENT_VIEW_SETTINGS,
        component: EventViewSettings,
        props: (route) => ({
          event: route.meta.event,
        }),
      },
      {
        path: 'user-fields-editor',
        name: routesNames.EVENT_VIEW_USER_FIELDS_EDITOR,
        component: EventViewUserFieldsEditor,
        props: (route) => ({
          event: route.meta.event,
        }),
      },
      {
        path: 'banner',
        name: routesNames.EVENT_VIEW_BANNER,
        component: EventViewBanner,
        props: (route) => ({
          event: route.meta.event,
        }),
      },
      {
        path: 'privacy-policy',
        name: routesNames.EVENT_VIEW_PRIVACY_POLICY,
        component: EventViewPrivacyPolicy,
        props: (route) => ({
          event: route.meta.event,
        }),
      },
      {
        path: 'check-in',
        name: routesNames.EVENT_VIEW_CHECK_IN,
        component: EventViewCheckIn,
        props: (route) => ({
          event: route.meta.event,
        }),
      },
      {
        path: 'mailing',
        name: routesNames.EVENT_VIEW_MAILING,
        component: EventViewMailing,
        props: (route) => ({
          event: route.meta.event,
        }),
      },
      {
        path: 'scu-configuration',
        name: routesNames.EVENT_VIEW_SCU,
        component: EventViewScuConfig,
        props: (route) => ({
          event: route.meta.event,
        }),
      },
      {
        path: 'survey-settings',
        name: routesNames.EVENT_VIEW_SURVEY_SETTINGS,
        component: EventViewSurveySettings,
        props: (route) => ({
          event: route.meta.event,
        }),
      },
      {
        path: 'users',
        name: routesNames.EVENT_VIEW_USERS,
        component: EventViewUsers,
        props: (route) => ({
          event: route.meta.event,
        }),
      },
      {
        path: 'import-users',
        name: routesNames.EVENT_VIEW_IMPORT_USERS,
        component: EventViewImportUsers,
        props: (route) => ({
          event: route.meta.event,
        }),
      },
      {
        path: 'survey',
        name: routesNames.EVENT_VIEW_SURVEY,
        component: EventViewSurvey,
      },
      {
        path: 'survey/question-type/:questionTypeId',
        name: routesNames.EVENT_VIEW_SURVEY_EDIT_QUESTIONS,
        component: EditSurveyQuestionsView,
        props: (route) => ({
          questionType: route.meta.questionType,
        }),
      },
      {
        path: 'edit-survey/reorder',
        name: routesNames.EVENT_VIEW_SURVEY_QUESTION_TYPES_REORDER,
        component: ReorderSurveyQuestionTypesView,
        props: (route) => ({
          questionTypes: route.meta.questionTypes,
        }),
      },
      {
        path: 'marketing-emails',
        name: routesNames.EVENT_VIEW_MARKETING_EMAILS,
        component: EventViewMarketingEmails,
        props: (route) => ({
          event: route.meta.event,
        }),
      },
    ],
  },
  {
    path: '/event/:id/checkin/:locale?',
    name: routesNames.CHECK_IN,
    components: {
      default: CheckInView,
    },
    props: {
      default: (route) => ({
        event: route.meta.event,
      }),
    },
  },
  {
    path: '/register/:locale?',
    name: routesNames.REGISTER,
    components: {
      default: RegisterFormView,
    },
    props: {
      default: (route) => ({
        event: route.meta.mainEvent,
        eventsList: route.meta.eventsList,
      }),
    },
  },
];

const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to) => {
  const promises = [];

  const fetchEventAndSetMeta = () => fetchEvent(to.params.id)
    .then((event) => { to.meta.event = event; return event; });

  switch (to.name) {
    case routesNames.EVENT_VIEW_SETTINGS:
      if (to.params.id === 'new') {
        promises.push(
          Promise.resolve({ speakers: [] }).then((event) => {
            to.meta.event = event;
            return event;
          }),
        );
      } else {
        promises.push(fetchEventAndSetMeta());
      }
      break;
    case routesNames.EVENT_VIEW_SURVEY_EDIT_QUESTIONS:
      promises.push(
        fetchQuestionTypeById(to.params.questionTypeId)
          .then((questionType) => { to.meta.questionType = questionType; }),
      );
      promises.push(fetchEventAndSetMeta());
      break;
    case routesNames.EVENT_VIEW_SURVEY_QUESTION_TYPES_REORDER:
      promises.push(
        fetchQuestionTypes(to.params.id)
          .then((questionTypes) => { to.meta.questionTypes = questionTypes; }),
      );
      promises.push(fetchEventAndSetMeta());
      break;
    case routesNames.REGISTER: {
      const idsArray = to.query.ids.split(',');
      const registerPromises = idsArray.map(async (id) => {
        const events = await fetchEvent(id);
        return events;
      });
      promises.push(Promise.all(registerPromises).then((eventsList) => {
        to.meta.eventsList = eventsList;
        [to.meta.mainEvent] = eventsList;
      }));
      break;
    }
    default:
      if (Object.values(routesNames).includes(to.name)) {
        promises.push(fetchEventAndSetMeta());
      }
      if (routesNames.CHECK_IN || routesNames.REGISTER) {
        i18n.global.locale.value = to.params.locale;
      }
      break;
  }

  if (promises.length) {
    await Promise.all(promises);
  }
});

export default router;
